import { QueryClient, InfiniteData } from "@tanstack/react-query";
import type { ChatItem } from "@/components/ai/chat-item";
import type { ChatListResponse } from "@/hooks/services/queries/ai-agent/use-chats-query";
import { normalizeKnowledgeContent, type KbContentData } from "@/utils/knowledge-content";

export interface UploadedFile {
  fileId: number;
  filename: string;
  documentId: string;
  mimeType: string;
  createdAt: string;
}

// 서버 메시지 타입
export interface ServerMessage {
  chatId: string;
  conversationTime: string;
  message: string;
  messageType: "USER" | "ASSISTANT";
  tool: string;
  chatType?: "DeepResearch";
  isGenerateFinalReport?: boolean;
  referencesContent?: { reference_number: number; title: string; url: string; content?: string }[];
  uploadedFiles?: UploadedFile[];
  sdfJobName?: string;
}

/**
 * assistant 메시지를 "덮어쓰기" 또는 "추가"하는 공통 유틸
 *
 * 사용 시점
 * - streaming token 수신
 * - final_report 수신
 * - assistant 메시지가 비어 있는 경우 보정
 *
 * 동작 방식
 * 1. 현재 messages 캐시를 가져온다
 * 2. 마지막 메시지가 ASSISTANT면 → 해당 메시지를 업데이트
 * 3. 아니면 새로운 ASSISTANT 메시지를 push
 *
 */

export const updateAssistantMessage = (
  queryClient: QueryClient,
  chatId: string,
  message: string,
  extra?: Partial<ServerMessage>
) => {
  const current =
    queryClient.getQueryData<ServerMessage[]>(["messages", chatId]) || [];

  const next = [...current];
  const last = next[next.length - 1];

  // 마지막 메시지가 assistant면 덮어쓰기
  if (last?.messageType === "ASSISTANT") {
    next[next.length - 1] = {
      ...last,
      message,
      ...extra,
    };
  } 
  // 마지막 메시지가 assistant가 아니면 새로 추가
  else {
    next.push({
      chatId,
      conversationTime: new Date().toISOString(),
      message,
      messageType: "ASSISTANT",
      tool: "",
      ...extra,
    });
  }

  queryClient.setQueryData(["messages", chatId], next);
};

/**
 * 새 채팅 시작 시 optimistic 메시지를 캐시에 즉시 추가
 *
 * 사용 시점
 * - 사용자가 메시지 전송 버튼을 누른 직후
 * - 서버 응답을 기다리기 전에 UI에 바로 반영
 *
 * 동작
 * - USER 메시지 1개
 * - 빈 ASSISTANT 메시지 1개 (streaming용)
 *
 *   주의
 * - 반드시 같은 sid(chatId) 기준으로만 사용해야 함
 * - backend에서 실제 chatId를 받으면 migrateTempChat으로 이동 필요
 */

export const appendOptimisticMessages = (
  queryClient: QueryClient,
  sid: string,
  userMessage: string,
  options?: { chatType?: "DeepResearch"; uploadedFiles?: UploadedFile[]; sdfJobName?: string }
) => {
  const prev = queryClient.getQueryData<ServerMessage[]>(["messages", sid]) ?? [];
  const now = new Date().toISOString();

  const assistantMessage: ServerMessage = {
    chatId: sid,
    conversationTime: now,
    message: "",
    messageType: "ASSISTANT",
    tool: "",
  };

  if (options?.chatType === "DeepResearch") {
    assistantMessage.chatType = "DeepResearch";
  }

  const userMsg: ServerMessage = {
    chatId: sid,
    conversationTime: now,
    message: userMessage,
    messageType: "USER",
    tool: "",
  };

  if (options?.uploadedFiles && options.uploadedFiles.length > 0) {
    userMsg.uploadedFiles = options.uploadedFiles;
  }

  if (options?.sdfJobName) {
    userMsg.sdfJobName = options.sdfJobName;
  }

  queryClient.setQueryData(["messages", sid], [
    ...prev,
    userMsg,
    assistantMessage,
  ]);
};

/**
 * temp chatId → 실제 chatId 로 캐시를 이동시키는 유틸
 *
 * 사용 시점
 * - streaming 중 backend 이벤트에서 실제 chatId를 받았을 때
 *
 * 이동 대상
 * 1. messages 캐시
 *    ["messages", tempId] → ["messages", realChatId]
 * 2. chats 목록
 *    sidebar에 보이는 chatId를 교체
 *
 *  주의
 * - 반드시 한 번만 호출해야 함
 * - 호출 후 temp 캐시는 제거됨
 */
export const migrateTempChat = (
  queryClient: QueryClient,
  oldSid: string,
  newChatId: string
) => {
  // messages 이동
  const messages = queryClient.getQueryData<ServerMessage[]>([
    "messages",
    oldSid,
  ]);

  if (messages) {
    queryClient.setQueryData(
      ["messages", newChatId],
      messages.map((m) => ({ ...m, chatId: newChatId }))
    );

    queryClient.removeQueries({ queryKey: ["messages", oldSid] });
  }

  // chats 목록 교체 (InfiniteQuery 구조)
  const chats = queryClient.getQueryData<InfiniteData<ChatListResponse>>(["chats"]);

  if (chats?.pages) {
    queryClient.setQueryData<InfiniteData<ChatListResponse>>(["chats"], {
      ...chats,
      pages: chats.pages.map((page) => ({
        ...page,
        content: page.content.map((c) =>
          c.chatId === oldSid ? { ...c, chatId: newChatId } : c
        ),
      })),
    });
  }
};
/** messages 캐시에 든 메시지. 서버 응답에서 온 id 와 정규화된 KB 자료가 붙는다 */
export type CachedMessage = ServerMessage & { id?: string | number; knowledgeContent?: KbContentData };

/**
 * 서버 메시지로 캐시를 맞춘 새 목록. 바꿀 것이 없으면 null.
 * - count 일치: 인덱스 기반 merge (캐시 필드 보존)
 * - count 불일치: 서버 데이터로 replace (인덱스 불일치 방지)
 */
export const mergeServerMessages = (
  current: CachedMessage[],
  raw: unknown
): CachedMessage[] | null => {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const serverMessages: CachedMessage[] = raw.map(normalizeKnowledgeContent);
  if (current.length === 0) return null;
  // 서버가 캐시보다 적은 메시지를 반환하면 DB 저장이 아직 안 된 것이므로 교체하지 않음
  if (serverMessages.length < current.length) return null;
  if (serverMessages.length !== current.length) {
    return serverMessages.map((server) => ({
      id: String(server.id ?? ""),
      chatId: server.chatId,
      conversationTime: server.conversationTime,
      message: server.message,
      messageType: server.messageType,
      tool: server.tool,
      chatType: server.chatType,
      isGenerateFinalReport: server.isGenerateFinalReport,
      referencesContent: server.referencesContent,
      knowledgeContent: server.knowledgeContent,
      uploadedFiles: server.uploadedFiles,
    }));
  }
  return current.map((msg, i) => {
    const server = serverMessages[i];
    if (!server) return msg;
    return {
      ...msg,
      id: String(server.id ?? msg.id ?? ""),
      chatId: server.chatId ?? msg.chatId,
      conversationTime: server.conversationTime ?? msg.conversationTime,
      message: server.message || msg.message,
      messageType: server.messageType ?? msg.messageType,
      tool: server.tool ?? msg.tool,
      chatType: server.chatType ?? msg.chatType,
      isGenerateFinalReport: msg.isGenerateFinalReport ?? server.isGenerateFinalReport,
      referencesContent: (server.referencesContent?.length ?? 0) > 0 ? server.referencesContent : msg.referencesContent,
      knowledgeContent: server.knowledgeContent ?? msg.knowledgeContent,
      uploadedFiles: server.uploadedFiles ?? msg.uploadedFiles,
    };
  });
};
